import { useState, useRef } from 'react';
import Axios from 'axios';
import * as Sentry from '@sentry/react';

import type { CancelToken } from 'axios';
import type { AssetContentTypeEnum } from 'src/graphql/schema';

const FILE_CHUNK_SIZE = 10000000;
const MAX_CONCURRENT_PARTS = 6;

type IUploadPart = {
  etag: string;
  partNumber: number;
};

export type IRepoUploadOperations = {
  init: (args: {
    numParts: number;
    filename: string;
    repoName?: string;
    assetId?: string;
  }) => Promise<{ uploadId?: string | null; uploadUrls?: Array<string> | null }>;
  complete: (args: {
    uploadId: string;
    parts: IUploadPart[];
    size: number;
    contentType: AssetContentTypeEnum;
    repoName?: string;
    assetId?: string;
  }) => Promise<{ assetId: string | null }>;
  cancel: (args: { uploadId: string; assetId?: string }) => Promise<unknown>;
};

const useRepoUploadFlow = (productId: string, operations: IRepoUploadOperations) => {
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadIdsInProgress, setUploadIdsInProgress] = useState<string[]>([]);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    percent: 0,
    currentCount: 0,
  });
  const cancelTokenSource = useRef<{
    token: CancelToken;
    cancel: (message: string) => void;
  } | null>(null);

  const repoUpload = async (file: File, contentType: AssetContentTypeEnum, repoName?: string, assetId?: string) => {
    const numParts = file.size ? Math.ceil(file.size / FILE_CHUNK_SIZE) : 1;
    const { name: filename } = file;

    setUploadStatus((status) => {
      return { ...status, [file.name]: 'uploading' };
    });

    const { uploadId, uploadUrls } = await operations.init({ numParts, filename, repoName, assetId });

    if (!uploadId) throw new Error('Failed to initialize repository upload');

    setUploadProgress((upload) => ({ ...upload, percent: 0 }));
    setUploadIdsInProgress((uploadIds) => [...uploadIds, uploadId]);

    const axios = Axios.create();
    delete axios.defaults.headers.put['Content-Type'];

    const keys = Object.keys(uploadUrls || {});
    const parts: IUploadPart[] = new Array(keys.length);
    let loadedParts = 0;

    const uploadPart = async (index: number) => {
      const start = index * FILE_CHUNK_SIZE;
      const end = (index + 1) * FILE_CHUNK_SIZE;
      const blob = index < keys.length - 1 ? file.slice(start, end) : file.slice(start);
      const uploadUrl = uploadUrls?.[index] ?? '';

      if (!uploadUrl) throw new Error('Missing upload URL for repository part');

      const res = await axios.put<IUploadPart>(uploadUrl, blob, {
        cancelToken: cancelTokenSource.current?.['token'],
        ...(numParts === 1 && {
          onUploadProgress: ({ progress = 0 }) => {
            const percent = Math.floor(progress * 100);
            setUploadProgress((upload) => ({ ...upload, percent }));
          },
        }),
      });

      if (numParts) {
        loadedParts++;

        setUploadProgress((upload) => ({
          ...upload,
          percent: Math.floor((loadedParts / numParts) * 100),
        }));
      }

      parts[index] = { etag: res.headers.etag, partNumber: index + 1 };
    };

    const indexes = keys.map((indexStr) => parseInt(indexStr));
    const runWorker = async () => {
      let index: number | undefined;
      while ((index = indexes.shift()) !== undefined) {
        await uploadPart(index);
      }
    };

    let uploadedAssetId: string | null = null;

    try {
      await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_PARTS, indexes.length) }, runWorker));

      const { assetId: completedAssetId } = await operations.complete({
        uploadId,
        parts,
        size: file.size,
        contentType,
        repoName,
        assetId,
      });

      uploadedAssetId = completedAssetId;
    } catch (e) {
      Sentry.captureException(e);

      await operations.cancel({ uploadId, assetId });

      throw e;
    } finally {
      setUploadIdsInProgress((uploadIds) => uploadIds.filter((uid) => uid !== uploadId));
    }

    setUploadStatus((status) => {
      return { ...status, [file.name]: 'uploaded' };
    });

    return { [file.name]: uploadedAssetId };
  };

  const cancelUpload = () => {
    cancelTokenSource.current?.cancel('Operation canceled by the user.');

    setUploadProgress({
      percent: 0,
      currentCount: 0,
    });

    uploadIdsInProgress.forEach((uploadId) => operations.cancel({ uploadId }));
  };

  const uploadFiles = async (files: File[], contentType: AssetContentTypeEnum, repoName?: string, assetId?: string) => {
    if (!productId) return;
    if (!files.length) return;

    setUploadCompleted(false);
    setUploadStatus(Object.fromEntries(files.map((file) => [file.name, 'new'])));
    setUploadProgress({
      percent: 0,
      currentCount: 1,
    });
    cancelTokenSource.current = Axios.CancelToken.source();

    let uploadedAssetIds: { [key: string]: string | null } = {};

    try {
      for (const file of files) {
        const uploadedAssetId = await repoUpload(file, contentType, repoName, assetId);
        if (uploadedAssetId) uploadedAssetIds = { ...uploadedAssetIds, ...uploadedAssetId };

        setUploadProgress((upload) => ({
          ...upload,
          currentCount: upload.currentCount === files.length ? files.length : upload.currentCount + 1,
        }));
      }

      setUploadCompleted(true);
    } finally {
      setUploadProgress({
        percent: 0,
        currentCount: 0,
      });
    }

    return uploadedAssetIds;
  };

  return [uploadFiles, cancelUpload, uploadStatus, uploadCompleted, uploadProgress] as const;
};

export default useRepoUploadFlow;
